/** An item paired with a key that is unique within its own list. */
export interface Keyed<TItem> {
  /** Key for a keyed `{#each}` — `keyOf(item)` plus this item's occurrence index. */
  key: string;
  /** The item this key belongs to. */
  item: TItem;
}

/** Joins a `keyOf` value to its occurrence counter. */
const OCCURRENCE_SEPARATOR = '#';

/** Records one more sighting of `candidate` and returns this occurrence's key. */
function nextKey(occurrences: Map<string, number>, candidate: string): string {
  const seenBefore = occurrences.get(candidate) ?? 0;
  occurrences.set(candidate, seenBefore + 1);
  return `${candidate}${OCCURRENCE_SEPARATOR}${String(seenBefore)}`;
}

/**
 * Pairs every item with a key unique across `items`: `keyOf(item)` plus that value's
 * zero-based occurrence. Splitting at the last separator recovers the pair, so the
 * encoding is injective. Occurrences follow list order, so a repeated `keyOf` value
 * ties its key to the position rather than to the item.
 */
export function withUniqueKeys<TItem>(
  items: readonly TItem[],
  keyOf: (item: TItem) => string,
): Keyed<TItem>[] {
  const occurrences = new Map<string, number>();
  return items.map((item) => ({ key: nextKey(occurrences, keyOf(item)), item }));
}
